import { useTranslation } from '@portals-platform/i18n';
import { FormField, FormGrid, SelectField, useFormContext } from '@portals-platform/ui-forms';
import type { ChangeEvent } from 'react';
import type { CertificateTemplateResponse } from '../../../data-access/api/responses.js';
import { applyTemplateSelection } from '../../../lib/new-certificate/defaults.js';
import { STATIC_FIELD_CONFIG } from '../../../lib/new-certificate/fields.js';
import { useSelectedCountryName } from '../../../lib/new-certificate/hooks/use-selected-country-name.js';
import { useSelectedPolicy } from '../../../lib/new-certificate/hooks/use-selected-policy.js';
import { useTemplateSectionState } from '../../../lib/new-certificate/hooks/use-template-section-state.js';
import type { FormValues } from '../../../lib/new-certificate/types.js';
import { CertificateSection } from './certificate-section.js';

interface TemplateSectionProps {
  templates: CertificateTemplateResponse[];
  isLoading: boolean;
}

export function TemplateSection({ templates, isLoading }: TemplateSectionProps) {
  const { t } = useTranslation();
  const { reset, getValues } = useFormContext<FormValues>();
  const { isCustomerInfoComplete, templateId } = useTemplateSectionState();
  const countryName = useSelectedCountryName();
  const selectedPolicy = useSelectedPolicy();

  const selectedTemplate = templates.find((template) => template.id === templateId) ?? null;

  const handleTemplateChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const template = templates.find((candidate) => candidate.id === event.target.value) ?? null;

    reset(applyTemplateSelection({ currentValues: getValues(), template, selectedPolicy }));
  };

  if (!isCustomerInfoComplete) {
    return null;
  }

  return (
    <CertificateSection
      title={t('certificates.new.template.title')}
      description={t('certificates.new.template.available', {
        count: templates.length,
        country: countryName,
      })}
    >
      <FormGrid>
        <FormField
          name="certificateTemplateId"
          label={t(STATIC_FIELD_CONFIG.certificateTemplateId.labelKey)}
          required
          col={12}
        >
          <SelectField
            name="certificateTemplateId"
            options={templates.map((template) => ({
              value: template.id,
              label: template.templateName,
            }))}
            placeholder={t(STATIC_FIELD_CONFIG.certificateTemplateId.placeholderKey)}
            disabled={isLoading}
            onChange={handleTemplateChange}
          />
        </FormField>
      </FormGrid>

      {selectedTemplate && (
        <div className="mt-4 rounded-[var(--radius-md)] bg-[color-mix(in_srgb,var(--color-info)_12%,transparent)] px-4 py-3">
          <p className="text-sm font-bold text-[var(--color-info)]">
            {selectedTemplate.templateName}
          </p>
          <p className="text-sm text-muted-foreground">
            {t('certificates.new.template.externalTemplate')}{' '}
            {selectedTemplate.externalTemplateName || '-'}
          </p>
        </div>
      )}
    </CertificateSection>
  );
}
